import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { qaApi } from '../api/apiClient';
import './AgendaPage.css';

function AgendaItemIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 3.75A2.75 2.75 0 0 0 4.25 6.5v11A2.75 2.75 0 0 0 7 20.25h10A2.75 2.75 0 0 0 19.75 17.5v-11A2.75 2.75 0 0 0 17 3.75H7Zm0 1.5h10c.69 0 1.25.56 1.25 1.25v11c0 .69-.56 1.25-1.25 1.25H7c-.69 0-1.25-.56-1.25-1.25v-11c0-.69.56-1.25 1.25-1.25Zm1.5 3.25a.75.75 0 0 1 .75-.75h5.5a.75.75 0 1 1 0 1.5h-5.5a.75.75 0 0 1-.75-.75Zm0 4a.75.75 0 0 1 .75-.75h5.5a.75.75 0 1 1 0 1.5h-5.5a.75.75 0 0 1-.75-.75Zm0 4a.75.75 0 0 1 .75-.75h3.5a.75.75 0 1 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75Z" />
    </svg>
  );
}

function formatDue(value) {
  if (!value) return 'No due date';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function dueClass(value) {
  if (!value) return '';
  const now = Date.now();
  const due = new Date(value).getTime();
  if (Number.isNaN(due)) return '';
  const diff = due - now;
  if (diff < 0) return 'is-overdue';
  if (diff <= 24 * 60 * 60 * 1000) return 'is-soon';
  return '';
}

export default function MyAgendaPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importSource, setImportSource] = useState('google');
  const [importPreview, setImportPreview] = useState([]);
  const [importingPreview, setImportingPreview] = useState(false);
  const [importingWorkspaces, setImportingWorkspaces] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [selectedPreviewKeys, setSelectedPreviewKeys] = useState([]);
  const [icsFileName, setIcsFileName] = useState('');
  const icsFileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await qaApi.listMyAgendaItems();
        if (!cancelled) setItems(res.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { overdue, soon, later } = useMemo(() => {
    const o = [];
    const s = [];
    const l = [];
    const now = Date.now();
    items.forEach((it) => {
      const due = it?.dueAt ? new Date(it.dueAt).getTime() : NaN;
      if (Number.isNaN(due)) {
        l.push(it);
      } else if (due < now) {
        o.push(it);
      } else if (due - now <= 24 * 60 * 60 * 1000) {
        s.push(it);
      } else {
        l.push(it);
      }
    });
    return { overdue: o, soon: s, later: l };
  }, [items]);

  const renderList = (list) => (
    <div className="v-agenda-list">
      {list.map((item) => {
        const wsId = item.workspaceId;
        const openThread = () => {
          const q = item.questionId;
          if (q != null) {
            navigate(`/workspaces/${wsId}/qa?questionId=${q}`);
          } else {
            navigate(`/workspaces/${wsId}/qa`);
          }
        };
        return (
          <div
            key={`${wsId}-${item.id}`}
            className={`v-agenda-card ${dueClass(item.dueAt)}`}
            role="button"
            tabIndex={0}
            onClick={openThread}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openThread();
              }
            }}
          >
            <div className="v-agenda-card__icon" aria-hidden>
              <AgendaItemIcon />
            </div>
            <div className="v-agenda-card__body">
              <div className="v-agenda-card__top">
                <h3>{item.title}</h3>
                <span className="v-badge v-badge--warning">{item.status || 'OPEN'}</span>
              </div>
              {item.workspaceName ? (
                <div className="v-agenda-card__workspace">{item.workspaceName}</div>
              ) : null}
              {item.questionPreview ? (
                <p className="v-agenda-card__thread">{item.questionPreview}</p>
              ) : null}
              {item.objective ? <p className="v-agenda-card__objective">{item.objective}</p> : null}
              <div className="v-agenda-card__meta">
                <span>Due: {formatDue(item.dueAt)}</span>
                {item.ownerUserName ? <span>Owner: {item.ownerUserName}</span> : null}
              </div>
            </div>

            <button
              type="button"
              className="v-agenda-card__open"
              onClick={(e) => {
                e.stopPropagation();
                openThread();
              }}
            >
              Open
            </button>
          </div>
        );
      })}
    </div>
  );

  if (loading) return <div className="v-loading">Loading agenda...</div>;

  const handleIcsPreview = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIcsFileName(file.name);
    setImportError('');
    setImportSuccess('');
    setImportingPreview(true);
    try {
      const res = await qaApi.previewAgendaImport(importSource, file);
      const items = res.data?.items || [];
      setImportPreview(items);
      setSelectedPreviewKeys(
        items
          .map((_, idx) => String(idx))
          .filter((k) => !(items[Number(k)]?.duplicateWorkspace))
      );
      if (items.length === 0) {
        setImportError('No calendar events found in this file.');
      }
    } catch (err) {
      setImportPreview([]);
      setIcsFileName('');
      setImportError(err?.response?.data?.message || 'Import preview failed. Please try a valid .ics export.');
    } finally {
      setImportingPreview(false);
      event.target.value = '';
    }
  };

  const handleCreateWorkspaces = async () => {
    const selectedItems = importPreview.filter((_, idx) => selectedPreviewKeys.includes(String(idx)));
    if (selectedItems.length === 0) {
      setImportError('Please select at least one agenda item to import.');
      return;
    }
    setImportError('');
    setImportSuccess('');
    setImportingWorkspaces(true);
    try {
      const payload = {
        source: importSource,
        items: selectedItems,
        createTextDocument: true,
      };
      const res = await qaApi.importAgendaToWorkspaces(payload);
      const count = Number(res.data?.createdCount || 0);
      const skipped = Number(res.data?.skippedCount || 0);
      setImportSuccess(`Created ${count} workspace(s). Skipped ${skipped} duplicate(s).`);
    } catch (err) {
      setImportError(err?.response?.data?.message || 'Could not create workspaces from agenda import.');
    } finally {
      setImportingWorkspaces(false);
    }
  };

  const togglePreviewSelection = (idx) => {
    const key = String(idx);
    setSelectedPreviewKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  return (
    <div className="v-page">
      <div className="v-breadcrumb">
        <button type="button" onClick={() => navigate('/workspaces')}>Workspaces</button>
        <span className="v-breadcrumb__sep">›</span>
        <span>My agenda</span>
      </div>
      <div className="v-page__header">
        <div>
          <h1 className="v-page__title">My agenda</h1>
          <p className="v-page__subtitle">
            All follow-up items from Q&A across every workspace you can access.
          </p>
        </div>
      </div>

      <section className="v-agenda-import" aria-labelledby="agenda-import-heading">
        <div className="v-agenda-import__head">
          <div>
            <h2 id="agenda-import-heading">Import Google / Outlook calendar</h2>
            <p className="v-agenda-import__lede">
              Turn exported calendar events into Ventus workspaces. Each workspace can include a starter note with event
              details and AI preparation tips.
            </p>
          </div>
          <button
            type="button"
            className="v-btn v-btn--ghost v-btn--sm v-agenda-import__toggle"
            onClick={() => setImportOpen((prev) => !prev)}
            aria-expanded={importOpen}
            aria-controls="agenda-import-content"
          >
            {importOpen ? 'Hide import' : 'Show import'}
          </button>
        </div>

        {!importOpen ? (
          <p className="v-agenda-import__collapsed-note">Import panel is collapsed. Click Show import to upload calendar files.</p>
        ) : (
          <>
            <div id="agenda-import-content" className="v-agenda-import__content">
              <div className="v-agenda-import__explainer">
                <h3 className="v-agenda-import__explainer-title">How this works</h3>
                <ol className="v-agenda-import__steps">
                  <li>
                    <strong>Pick where the file came from</strong> — Google Calendar, Outlook, or any generic{' '}
                    <code>.ics</code> file. This label is stored with the import (the file format is the same).
                  </li>
                  <li>
                    <strong>Upload your export</strong> — export from your calendar app as <code>.ics</code> / iCalendar,
                    then use <em>Choose calendar file</em> below.
                  </li>
                  <li>
                    <strong>Review the preview</strong> — Ventus lists events, flags duplicates if a workspace with the same
                    name already exists, and shows preparation suggestions (AI when configured, otherwise sensible defaults).
                  </li>
                  <li>
                    <strong>Create workspaces</strong> — tick the events you want (non-duplicates are pre-selected). One
                    click creates a workspace per event and adds an optional text note with suggestions inside it.
                  </li>
                </ol>
              </div>

              <div className="v-agenda-import__toolbar">
                <div className="v-agenda-import__field">
                  <label className="v-agenda-import__field-label" htmlFor="agenda-import-source">
                    Calendar source
                  </label>
                  <select
                    id="agenda-import-source"
                    className="v-agenda-import__select-input"
                    value={importSource}
                    onChange={(e) => setImportSource(e.target.value)}
                  >
                    <option value="google">Google Calendar</option>
                    <option value="outlook">Outlook Calendar</option>
                    <option value="ics">Other / generic ICS</option>
                  </select>
                </div>

                <div className="v-agenda-import__field v-agenda-import__field--file">
                  <span className="v-agenda-import__field-label" id="agenda-file-label">
                    Calendar file
                  </span>
                  <div className="v-agenda-import__file-picker">
                    <input
                      ref={icsFileInputRef}
                      id="agenda-import-file"
                      type="file"
                      className="v-agenda-import__file-input"
                      accept=".ics,.ical,text/calendar"
                      onChange={handleIcsPreview}
                      disabled={importingPreview}
                      aria-labelledby="agenda-file-label"
                    />
                    <button
                      type="button"
                      className="v-agenda-import__file-trigger"
                      onClick={() => icsFileInputRef.current?.click()}
                      disabled={importingPreview}
                    >
                      {importingPreview ? 'Reading file…' : 'Choose calendar file'}
                    </button>
                    <span className="v-agenda-import__file-status" aria-live="polite">
                      {importingPreview && icsFileName ? (
                        <>Loading <strong>{icsFileName}</strong>…</>
                      ) : importPreview.length > 0 && icsFileName ? (
                        <>
                          <strong>{icsFileName}</strong>
                          <span className="v-agenda-import__file-status-meta">
                            · {importPreview.length} event{importPreview.length === 1 ? '' : 's'} loaded
                          </span>
                        </>
                      ) : icsFileName ? (
                        <strong>{icsFileName}</strong>
                      ) : (
                        <span className="v-agenda-import__file-status--muted">No file selected yet</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="v-agenda-import__field v-agenda-import__field--action">
                  <span className="v-agenda-import__field-label v-agenda-import__field-label--action">Next step</span>
                  <button
                    type="button"
                    className="v-btn v-btn--primary v-agenda-import__create-btn"
                    onClick={handleCreateWorkspaces}
                    disabled={importingPreview || importingWorkspaces || selectedPreviewKeys.length === 0}
                  >
                    {importingWorkspaces
                      ? 'Creating workspaces…'
                      : `Create workspaces (${selectedPreviewKeys.length})`}
                  </button>
                </div>
              </div>
              {importError ? <div className="v-invite-message v-invite-message--error">{importError}</div> : null}
              {importSuccess ? <div className="v-invite-message v-invite-message--success">{importSuccess}</div> : null}
              {importingPreview ? <div className="v-loading">Reading calendar export...</div> : null}

              {importPreview.length > 0 && (
                <div className="v-agenda-import__preview">
                  <h3>Import preview ({importPreview.length})</h3>
                  <div className="v-agenda-list">
                    {importPreview.map((ev, idx) => (
                      <div key={`${ev.externalId || ev.title}-${idx}`} className="v-agenda-card">
                        <div className="v-agenda-card__top">
                          <h3>
                            <label className="v-agenda-import__select">
                              <input
                                type="checkbox"
                                checked={selectedPreviewKeys.includes(String(idx))}
                                onChange={() => togglePreviewSelection(idx)}
                                disabled={ev.duplicateWorkspace}
                              />
                              <span>{ev.title}</span>
                            </label>
                          </h3>
                          {ev.duplicateWorkspace ? (
                            <span className="v-badge v-badge--warning">Duplicate</span>
                          ) : (
                            <span className="v-badge v-badge--info">{importSource}</span>
                          )}
                        </div>
                        {ev.duplicateWorkspace && (
                          <p className="v-agenda-card__thread">
                            Already exists as workspace: <strong>{ev.existingWorkspaceName || ev.title}</strong>
                          </p>
                        )}
                        <div className="v-agenda-card__meta">
                          <span>Start: {formatDue(ev.startAt)}</span>
                          {ev.endAt ? <span>End: {formatDue(ev.endAt)}</span> : null}
                          {ev.location ? <span>Location: {ev.location}</span> : null}
                        </div>
                        {ev.description ? <p className="v-agenda-card__objective">{ev.description}</p> : null}
                        {Array.isArray(ev.preparationSuggestions) && ev.preparationSuggestions.length > 0 && (
                          <ul className="v-agenda-import__suggestions">
                            {ev.preparationSuggestions.map((s, i) => (
                              <li key={`${idx}-${i}`}>{s}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {items.length === 0 ? (
        <div className="v-empty">
          <h3>No agenda items yet</h3>
          <p>Create agenda items from Q&A threads inside a workspace, or open a workspace agenda.</p>
        </div>
      ) : (
        <div className="v-agenda-sections">
          {overdue.length > 0 && (
            <section>
              <h2 className="v-agenda-section-title">Overdue</h2>
              {renderList(overdue)}
            </section>
          )}
          {soon.length > 0 && (
            <section>
              <h2 className="v-agenda-section-title">Due within 24 hours</h2>
              {renderList(soon)}
            </section>
          )}
          {later.length > 0 && (
            <section>
              <h2 className="v-agenda-section-title">Later</h2>
              {renderList(later)}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
